import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography
} from "@material-ui/core";
import { Brush as BrushIcon } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import useAuth from "hooks/useAuth";
import { db } from "firebaseConfig";
import { GenericConverter } from "utils/firestoreConverters";
import CreativesGeneratorModal from "components/_dashboard/creatives-generator/CreativesGeneratorModal";
import { CreativeCard } from "components/_dashboard/creatives-generator/CreativeCard";
import { CreativeViewDialog } from "components/_dashboard/creatives-generator/CreativeViewDialog";
import {
  isWithinEditWindow,
  getRemainingEditTimeSeconds
} from "components/_dashboard/creatives-generator/creativeHelpers";
import {
  GeneratedUserCreative,
  GENERATED_USER_CREATIVES_COLLECTION_NAME
} from "../../../../@types/sharedSchema";

type ListingCreativesTabProps = {
  listingId: string;
};

export function ListingCreativesTab({ listingId }: ListingCreativesTabProps): JSX.Element {
  const { t: translate } = useTranslation();
  const {
    user: { email }
  } = useAuth();

  const [creatives, setCreatives] = useState<GeneratedUserCreative[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewImageUrl, setViewImageUrl] = useState<string | null>(null);
  const [viewedCreative, setViewedCreative] = useState<GeneratedUserCreative | null>(null);
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [editCreativeData, setEditCreativeData] = useState<{
    imageUrl: string;
    creative: GeneratedUserCreative;
  } | null>(null);
  const [countdownTimers, setCountdownTimers] = useState<Record<string, number>>({});

  const isAdminUser = email === "haimbendanan@gmail.com";

  useEffect(() => {
    if (!email || !listingId) return;

    setLoading(true);

    const unsubscribe = db
      .collection(GENERATED_USER_CREATIVES_COLLECTION_NAME)
      .where("userId", "==", email)
      .where("listingIds", "array-contains", listingId)
      .withConverter(GenericConverter<GeneratedUserCreative>())
      .onSnapshot(
        (snapshot) => {
          const data = snapshot.docs
            .map((doc) => doc.data())
            .filter(
              (c) =>
                c.status !== "deleted" &&
                c.status !== "overwritten-by-regeneration" &&
                c.creativeType !== "makeover"
            )
            .sort((a, b) => {
              const aTime = a.createdTime?.getTime?.() ?? 0;
              const bTime = b.createdTime?.getTime?.() ?? 0;
              return bTime - aTime;
            });
          setCreatives(data);
          setLoading(false);
        },
        (err) => {
          console.error("Error fetching creatives for listing", err);
          setLoading(false);
        }
      );

    return unsubscribe;
  }, [email, listingId]);

  // Tick edit-window countdown timers every second
  useEffect(() => {
    const interval = setInterval(() => {
      const next: Record<string, number> = {};
      creatives.forEach((c) => {
        if (c.createdTime && c.status === "completed" && isWithinEditWindow(c.createdTime)) {
          next[c.id!] = getRemainingEditTimeSeconds(c.createdTime);
        }
      });
      setCountdownTimers(next);
    }, 1000);
    return () => clearInterval(interval);
  }, [creatives]);

  const handleViewImage = (imageUrl: string, creative: GeneratedUserCreative): void => {
    setViewImageUrl(imageUrl);
    setViewedCreative(creative);
  };

  const handleCloseImageView = (): void => {
    setViewImageUrl(null);
    setViewedCreative(null);
  };

  const handleEditClick = (creative: GeneratedUserCreative): void => {
    setEditCreativeData({ imageUrl: creative.generatedCreativeUrl, creative });
    setGeneratorOpen(true);
  };

  const handleGeneratorClose = (): void => {
    setGeneratorOpen(false);
    setEditCreativeData(null);
  };

  const handleDownload = async (imageUrl: string): Promise<void> => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `creative-${new Date().toISOString()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Error downloading creative", err);
      window.open(imageUrl, "_blank");
    }
  };

  const handleCopyPrompt = async (creative: GeneratedUserCreative): Promise<void> => {
    if (!creative.prompt) return;
    try {
      await navigator.clipboard.writeText(creative.prompt);
    } catch (err) {
      console.error("Error copying prompt", err);
    }
  };

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="flex-start">
        <Button
          variant="contained"
          color="primary"
          startIcon={<BrushIcon />}
          onClick={() => setGeneratorOpen(true)}
        >
          {translate("creativesGenerator.createNew", "יצירת קריאטיב")}
        </Button>
      </Stack>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : creatives.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            {translate("listingDetail.creatives.empty", "עדיין לא נוצרו קריאטיבים לנכס זה")}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {creatives.map((creative) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={creative.id}>
              <CreativeCard
                creative={creative}
                countdownSeconds={countdownTimers[creative.id!]}
                isAdminUser={isAdminUser}
                onView={handleViewImage}
                onEdit={handleEditClick}
                onDownload={handleDownload}
                onCopyPrompt={handleCopyPrompt}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <CreativesGeneratorModal
        open={generatorOpen}
        onClose={handleGeneratorClose}
        listingId={listingId}
        editMode={editCreativeData}
      />

      <CreativeViewDialog
        imageUrl={viewImageUrl}
        creative={viewedCreative}
        onClose={handleCloseImageView}
        onEdit={handleEditClick}
        onDownload={handleDownload}
      />
    </Stack>
  );
}
